import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, FormGroup, FormLabel, FormControl, FormSelect } from '../components/adminModals';
import ReportRecordsTable from '../components/ReportRecordsTable';
import ReportActionModal from '../components/ReportActionModal';
import { useNotification } from '../../sharedComponents/Notifications';
import {
    reportBatchService,
    ApprovalReportData,
    GenerateReportPayload,
    CreateReportBatchPayload,
    PeriodType,
    Quarter,
    ApprovalType,
    APPROVAL_TYPE_LABELS,
} from '../services/adminAuth';
import { ArrowLeft } from 'lucide-react';

// ── Quarter date ranges (month indices are 0-based) ─────────────────────────────

const QUARTER_RANGES: Record<number, { startMonth: number; startDay: number; endMonth: number; endDay: number }> = {
    [Quarter.Q1]: { startMonth: 0, startDay: 1, endMonth: 2, endDay: 31 },
    [Quarter.Q2]: { startMonth: 3, startDay: 1, endMonth: 5, endDay: 30 },
    [Quarter.Q3]: { startMonth: 6, startDay: 1, endMonth: 8, endDay: 30 },
    [Quarter.Q4]: { startMonth: 9, startDay: 1, endMonth: 11, endDay: 31 },
};

const QUARTER_OPTIONS = [
    { value: Quarter.Q1, label: 'Q1 (Jan - Mar)' },
    { value: Quarter.Q2, label: 'Q2 (Apr - Jun)' },
    { value: Quarter.Q3, label: 'Q3 (Jul - Sep)' },
    { value: Quarter.Q4, label: 'Q4 (Oct - Dec)' },
];

const toIsoUtc = (year: number, month: number, day: number): string =>
    new Date(Date.UTC(year, month, day, 12, 0, 0)).toISOString();

function GenerateReport() {
    const navigate = useNavigate();
    const { showNotification } = useNotification();

    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

    // ── Form state ───────────────────────────────────────────────────────────────
    const [approvalType, setApprovalType] = useState<number>(ApprovalType.SuccessfulSubmissions);
    const [periodType, setPeriodType] = useState<number>(PeriodType.Quarterly);
    const [year, setYear] = useState<number>(currentYear);
    const [quarter, setQuarter] = useState<number>(Quarter.Q1);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const [generating, setGenerating] = useState(false);
    const [creating, setCreating] = useState(false);
    const [reportData, setReportData] = useState<ApprovalReportData | null>(null);
    const [selectedRefIds, setSelectedRefIds] = useState<Set<string>>(new Set());
    // Which create action the confirmation modal is for: 'draft' (isSubmitted:false) or 'submit' (isSubmitted:true).
    const [confirmMode, setConfirmMode] = useState<'draft' | 'submit' | null>(null);

    const isQuarterly = periodType === PeriodType.Quarterly;
    const details = reportData?.approvalRequestDetail ?? [];
    const allSelected = details.length > 0 && details.every(d => selectedRefIds.has(d.referenceId));

    // Returns { fromDate, toDate, quarter? } from the current form, or null if invalid.
    const computePeriod = (): { fromDate: string; toDate: string; quarter?: number } | null => {
        if (isQuarterly) {
            const r = QUARTER_RANGES[quarter];
            return { fromDate: toIsoUtc(year, r.startMonth, r.startDay), toDate: toIsoUtc(year, r.endMonth, r.endDay), quarter };
        }
        if (!startDate || !endDate) {
            showNotification('warning', { title: 'Validation Error', message: 'Please select both a start and end date.' });
            return null;
        }
        if (new Date(startDate) > new Date(endDate)) {
            showNotification('warning', { title: 'Validation Error', message: 'Start date cannot be after the end date.' });
            return null;
        }
        return { fromDate: new Date(`${startDate}T12:00:00Z`).toISOString(), toDate: new Date(`${endDate}T12:00:00Z`).toISOString() };
    };

    const handleGenerate = async () => {
        const period = computePeriod();
        if (!period) return;

        setGenerating(true);
        try {
            const payload: GenerateReportPayload = { fromDate: period.fromDate, toDate: period.toDate, approvalType };
            const response = await reportBatchService.generateReportForApproval(payload);
            if (response.isSuccess) {
                setReportData(response.data);
                // Default to all records selected.
                setSelectedRefIds(new Set((response.data.approvalRequestDetail ?? []).map(d => d.referenceId)));
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to generate report' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An error occurred while generating the report' });
        } finally {
            setGenerating(false);
        }
    };

    const toggleRef = (referenceId: string) => {
        setSelectedRefIds(prev => {
            const next = new Set(prev);
            if (next.has(referenceId)) next.delete(referenceId);
            else next.add(referenceId);
            return next;
        });
    };

    const toggleSelectAll = () => {
        setSelectedRefIds(allSelected ? new Set() : new Set(details.map(d => d.referenceId)));
    };

    // Validate selection + period, then open the matching confirmation modal.
    const openConfirm = (mode: 'draft' | 'submit') => {
        if (selectedRefIds.size === 0) {
            showNotification('warning', { title: 'Validation Error', message: 'Please select at least one record to include.' });
            return;
        }
        if (!computePeriod()) return; // surfaces its own validation notification
        setConfirmMode(mode);
    };

    const handleCreate = async () => {
        if (!confirmMode) return;
        const period = computePeriod();
        if (!period) return;

        const isSubmitted = confirmMode === 'submit';
        setCreating(true);
        try {
            const payload: CreateReportBatchPayload = {
                referenceIds: Array.from(selectedRefIds),
                approvalType,
                fromDate: period.fromDate,
                toDate: period.toDate,
                periodType,
                isSubmitted,
                ...(period.quarter ? { quarter: period.quarter } : {}),
            };
            const response = await reportBatchService.createReportBatch(payload);
            if (response.isSuccess) {
                showNotification('success', {
                    title: 'Success',
                    message: isSubmitted ? 'Report batch created and marked ready for submission' : 'Draft saved successfully',
                    timer: 3000,
                });
                setConfirmMode(null);
                navigate('/admin/approval-requests');
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to create report batch' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An error occurred while creating the report batch' });
        } finally {
            setCreating(false);
        }
    };


    return (
        <div className="w-full">
            <div className="flex flex-wrap -mx-[15px]">
                <div className="w-full px-[15px]">
                    <button
                        onClick={() => navigate('/admin/approval-requests')}
                        className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                    >
                        <ArrowLeft size={16} />
                        Back to Approval Requests
                    </button>

                    <div className="mb-6">
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">New Report Batch</h1>
                        <p className="text-gray-500 text-sm sm:text-base">
                            Generate the records for a report type and period, pick the ones to include, then save it as an
                            editable draft or mark it ready for submission.
                        </p>
                    </div>

                    {/* Form card */}
                    <div className="bg-white rounded-[8px] shadow-[0_1px_3px_rgba(0,0,0,0.1)] p-4 sm:p-6 mb-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <FormGroup>
                                <FormLabel required>Report Type</FormLabel>
                                <FormSelect value={approvalType.toString()} onChange={(e) => setApprovalType(parseInt(e.target.value))} disabled={generating}>
                                    {Object.entries(APPROVAL_TYPE_LABELS)
                                        .filter(([value]) => Number(value) !== ApprovalType.ReceivedRevisedCopy)
                                        .map(([value, label]) => (
                                            <option key={value} value={value}>{label}</option>
                                        ))}
                                </FormSelect>
                            </FormGroup>

                            <FormGroup>
                                <FormLabel required>Period Type</FormLabel>
                                <div className="bg-gray-100 rounded-lg p-1 flex">
                                    <button
                                        type="button"
                                        onClick={() => setPeriodType(PeriodType.Quarterly)}
                                        className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${isQuarterly ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                    >
                                        Quarterly
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setPeriodType(PeriodType.Custom)}
                                        className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${!isQuarterly ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                    >
                                        Custom
                                    </button>
                                </div>
                            </FormGroup>

                            {isQuarterly ? (
                                <>
                                    <FormGroup>
                                        <FormLabel required>Year</FormLabel>
                                        <FormSelect value={year.toString()} onChange={(e) => setYear(parseInt(e.target.value))} disabled={generating}>
                                            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                                        </FormSelect>
                                    </FormGroup>
                                    <FormGroup>
                                        <FormLabel required>Quarter</FormLabel>
                                        <FormSelect value={quarter.toString()} onChange={(e) => setQuarter(parseInt(e.target.value))} disabled={generating}>
                                            {QUARTER_OPTIONS.map(q => <option key={q.value} value={q.value}>{q.label}</option>)}
                                        </FormSelect>
                                    </FormGroup>
                                </>
                            ) : (
                                <>
                                    <FormGroup>
                                        <FormLabel required>Start Date</FormLabel>
                                        <FormControl type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} disabled={generating} />
                                    </FormGroup>
                                    <FormGroup>
                                        <FormLabel required>End Date</FormLabel>
                                        <FormControl type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} disabled={generating} />
                                    </FormGroup>
                                </>
                            )}
                        </div>

                        <div className="flex justify-end mt-2">
                            <Button variant="success" onClick={handleGenerate} className="w-full sm:w-auto" disabled={generating}>
                                {generating ? 'Generating...' : 'Generate Report'}
                            </Button>
                        </div>
                    </div>

                    {/* Results */}
                    {reportData && (
                        <div className="bg-white rounded-[8px] shadow-[0_1px_3px_rgba(0,0,0,0.1)] px-[16px] sm:px-[24px] py-[16px] sm:py-[20px]">
                            <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <div>
                                    <h2 className="text-[16px] sm:text-[18px] font-[700] text-[#0A0A0A] m-0 mb-1">Generated Records</h2>
                                    <p className="text-xs sm:text-sm text-gray-500">
                                        <span className="font-semibold text-gray-700">{selectedRefIds.size}</span> of {details.length} selected
                                    </p>
                                </div>
                                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                                    <Button variant="white" onClick={() => openConfirm('draft')} className="w-full sm:w-auto" disabled={creating || selectedRefIds.size === 0}>
                                        Save as Draft
                                    </Button>
                                    <Button variant="success" onClick={() => openConfirm('submit')} className="w-full sm:w-auto" disabled={creating || selectedRefIds.size === 0}>
                                        Create &amp; Mark Ready
                                    </Button>
                                </div>
                            </div>

                            <ReportRecordsTable
                                data={reportData}
                                loading={false}
                                selectable
                                selectedRefIds={selectedRefIds}
                                onToggle={toggleRef}
                                onToggleAll={toggleSelectAll}
                                allSelected={allSelected}
                                reportType={approvalType}
                                emptyMessage="No records found for the selected report type and period."
                            />
                        </div>
                    )}

                    {/* Save Draft / Mark Ready confirmation */}
                    <ReportActionModal
                        isOpen={confirmMode !== null}
                        mode={confirmMode === 'submit' ? 'ready' : confirmMode === 'draft' ? 'draft' : null}
                        onClose={() => setConfirmMode(null)}
                        onConfirm={handleCreate}
                        isSubmitting={creating}
                        title={APPROVAL_TYPE_LABELS[approvalType]}
                        detail={`${selectedRefIds.size} record${selectedRefIds.size === 1 ? '' : 's'} selected`}
                    />
                </div>
            </div>
        </div>
    );
}

export default GenerateReport;
